import { email as emailRegex, phoneNumberReserveList } from '../utils/validation';
import { cleanPhoneNumber, isPhoneNumber, removePhoneCountryCode } from '../utils';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ServiceCapability } from 'shared/types/graphql';
import { useApolloClient } from '@apollo/client';
import {
  GetClinicPhoneNumbersDocument,
  GetClinicPhoneNumbersQuery,
  GetClinicPhoneNumbersQueryVariables,
} from '../types/graphql';

export enum ValidationResponseCodes {
  PhoneNumberValid = 'PhoneNumberValid',
  PhoneNumberInvalidFormat = 'PhoneNumberInvalidFormat',
  PhoneNumberInUse = 'PhoneNumberInUse',
  EmailValid = 'EmailValid',
  EmailEmpty = 'EmailEmpty',
  EmailInvalidFormat = 'EmailInvalidFormat',
}

interface ValidationResponse {
  isValid: boolean;
  code: ValidationResponseCodes;
  message?: string;
}

export const useValidation = (): {
  validatePhoneNumber: (phoneNumber: string) => Promise<ValidationResponse>;
  validateEmail: (email: string) => ValidationResponse;
} => {
  const { currentClinicId } = useClinicUser();
  const apolloClient = useApolloClient();

  const validatePhoneNumber = async (phoneNumber: string): Promise<ValidationResponse> => {
    const cleanedNumber = cleanPhoneNumber(phoneNumber);

    const { data: clinicPhoneNumbersData } = await apolloClient.query<
      GetClinicPhoneNumbersQuery,
      GetClinicPhoneNumbersQueryVariables
    >({
      query: GetClinicPhoneNumbersDocument,
      variables: {
        where: {
          clinicId: { equals: currentClinicId },
          isDeleted: { equals: false },
          capability: { equals: ServiceCapability.Sms },
        },
      },
    });

    const clinicPhoneNumbers =
      clinicPhoneNumbersData?.findManyClinicPhoneNumber?.map((phoneNumber) =>
        removePhoneCountryCode(cleanPhoneNumber(phoneNumber.number)),
      ) || [];

    if (!isPhoneNumber(cleanedNumber)) {
      return {
        isValid: false,
        code: ValidationResponseCodes.PhoneNumberInvalidFormat,
        message: 'Phone number not valid.',
      };
    }

    const reserveList = [...phoneNumberReserveList, ...clinicPhoneNumbers];
    const inUse: boolean = reserveList.includes(cleanedNumber);

    return {
      isValid: !inUse,
      code: !inUse ? ValidationResponseCodes.PhoneNumberValid : ValidationResponseCodes.PhoneNumberInUse,
      message: !inUse ? '' : 'Phone number already in use.',
    };
  };

  const validateEmail = (email: string): ValidationResponse => {
    const isValid = emailRegex.test(email.trim());
    return {
      isValid,
      code: isValid ? ValidationResponseCodes.EmailValid : ValidationResponseCodes.EmailInvalidFormat,
    };
  };

  return { validatePhoneNumber, validateEmail };
};
